import {
    Component,
    h,
    Prop,
    Host,
    State,
    Listen,
    Element,
    Watch,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { getPlaceholderUrl } from "../../utils/getPlaceholderUrl";
import { getViewportWidth } from "../../utils/getViewportWidth";

@Component({
    tag: "ux-iws-interactive-tiles",
    styleUrl: "interactive-tiles.scss",
})
export class InteractiveTiles {
    private baseClass = "ux-iws-interactive-tiles";

    @Element() element: HTMLUxIwsInteractiveTilesElement;

    /** the url for the background image for mobile viewports (<=567px) */
    @Prop() backgroundImageS: string;
    /** the url for the background image for tablet viewports (567px<x<1024px)*/
    @Prop() backgroundImageM: string;
    /** the url for the background image for large viewports (>=1024px)*/
    @Prop() backgroundImageL: string;
    /** if set, the content of the tiles will be centered */
    @Prop() contentCentered: boolean = false;

    @State() viewportWidth: number = 0;
    @State() backgroundImage: string = "";
    @State() lazyLoadReady: boolean = false;
    @State() backgroundBreakpoint: string;
    @State() tilesHeights: number[] = [];
    @State() elementHeight: number = null;
    @State() tiles: HTMLUxIwsInteractiveTileElement[] = null;

    componentWillLoad() {
        this.viewportWidth = getViewportWidth();
        this.tiles = Array.from(
            this.element.querySelectorAll("ux-iws-interactive-tile")
        );
    }

    componentDidLoad() {
        this.getTilesHeights();
    }

    @Watch("viewportWidth")
    adjustBackgroundLink() {
        if (this.viewportWidth < 568) {
            this.backgroundImage = this.backgroundImageS;
            this.backgroundBreakpoint = "s";
        } else if (this.viewportWidth > 1023) {
            this.backgroundImage = this.backgroundImageL;
            this.backgroundBreakpoint = "l";
        } else {
            this.backgroundImage = this.backgroundImageM;
            this.backgroundBreakpoint = "m";
        }
    }

    @Listen("resize", { target: "window" })
    handleResize() {
        this.onResize();
    }

    private onResize = debounce(() => {
        this.viewportWidth = getViewportWidth();
        this.getTilesHeights();
    }, 800);

    @Listen("lazybeforeunveil", { target: "document" })
    handleBackgroundLazyload(e: Event) {
        const eventTarget = e.target;
        if (
            (eventTarget as HTMLElement).closest("ux-iws-interactive-tiles") !==
            this.element
        )
            return;
        this.lazyLoadReady = true;
    }

    private getTilesHeights = () => {
        this.tilesHeights = [];
        this.tiles.forEach(async (tile: HTMLUxIwsInteractiveTileElement) => {
            if (!tile.interactive) return;
            const contentHeight = await tile.getContentHeight();
            const isTileActive = tile.active;
            const contentWrapperHeight = await tile.getContentWrapperHeight();
            const height = isTileActive
                ? contentWrapperHeight
                : contentHeight + contentWrapperHeight;
            this.tilesHeights = [...this.tilesHeights, height];
        });
    };

    @Watch("tilesHeights")
    setElementHeight() {
        if (this.tilesHeights.length < this.tiles.length) return;
        this.elementHeight =
            this.viewportWidth > 1023 ? Math.max(...this.tilesHeights) : null;
    }

    render() {
        const backgroundImage =
            this.lazyLoadReady && this.backgroundImage !== undefined
                ? `url(${this.backgroundImage})`
                : null;

        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--content-centered`]:
                        this.contentCentered,
                }}
                style={{
                    height:
                        this.elementHeight === null
                            ? "auto"
                            : `${this.elementHeight}px`,
                }}
            >
                <div
                    class={`${this.baseClass}__background-placeholder`}
                    style={{
                        background: `url('${getPlaceholderUrl(
                            this.element.clientWidth,
                            this.element.clientHeight
                        )}'`,
                    }}
                ></div>
                <div
                    class={{
                        [`${this.baseClass}__background`]: true,
                        [`${this.baseClass}__background--${this.backgroundBreakpoint}`]: true,
                        lazyload: true,
                    }}
                    style={
                        backgroundImage !== null
                            ? {
                                  backgroundImage: backgroundImage,
                              }
                            : {
                                  background: `url('${getPlaceholderUrl(
                                      this.element.clientWidth,
                                      this.element.clientHeight
                                  )}'`,
                              }
                    }
                ></div>
                <slot />
            </Host>
        );
    }
}
